import React from 'react';
import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { CONTROL, PRES, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { focusVisibleSx } from '../ui/focus';
import { AdminSectionId } from './adminSettingsState';

export interface AdminNavItem {
  id: AdminSectionId;
  label: string;
  description: string;
  dirty: boolean;
}

interface AdminSectionNavProps {
  items: AdminNavItem[];
  section: AdminSectionId;
  onSelect: (id: AdminSectionId) => void;
}

const AdminSectionNav: React.FC<AdminSectionNavProps> = ({ items, section, onSelect }) => (
  <>
    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
      <Typography sx={{ mb: 0.75, color: PRES.textSecondary, fontFamily: TYPE.ui, ...TYPE.label }}>
        {COPY.settingsSection}
      </Typography>
      <Select
        size="small"
        fullWidth
        value={section}
        onChange={(event) => onSelect(event.target.value as AdminSectionId)}
        sx={{ bgcolor: PRES.surface, fontFamily: TYPE.ui }}
      >
        {items.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.dirty ? `${item.label} (${COPY.settingsUnsavedShort})` : item.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
    <Box
      component="nav"
      aria-label={COPY.settings}
      sx={{
        display: { xs: 'none', md: 'block' },
        width: 248,
        flexShrink: 0,
        borderRight: `1px solid ${PRES.borderSubtle}`,
        bgcolor: PRES.surface,
        p: 1.5,
        overflow: 'auto',
      }}
    >
      {items.map((item) => {
        const active = section === item.id;
        return (
          <Button
            key={item.id}
            fullWidth
            onClick={() => onSelect(item.id)}
            aria-current={active ? 'page' : undefined}
            sx={{
              display: 'block',
              textAlign: 'left',
              textTransform: 'none',
              mb: 0.75,
              px: 1.25,
              py: 1,
              borderRadius: `${CONTROL.radius}px`,
              color: PRES.textPrimary,
              bgcolor: active ? PRES.surfaceSunken : 'transparent',
              border: active ? `1px solid ${PRES.borderSubtle}` : '1px solid transparent',
              ...focusVisibleSx,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ fontFamily: TYPE.ui, fontSize: 13, fontWeight: active ? 650 : 550 }}>
                {item.label}
              </Typography>
              {item.dirty && (
                <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.warn }}>
                  {COPY.settingsUnsavedShort}
                </Typography>
              )}
            </Box>
            <Typography sx={{ mt: 0.25, color: PRES.textTertiary, fontFamily: TYPE.ui, ...TYPE.caption, whiteSpace: 'normal' }}>
              {item.description}
            </Typography>
          </Button>
        );
      })}
    </Box>
  </>
);

export default AdminSectionNav;
